import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import apiClient, { API_ENDPOINTS } from '../../api/config';
import { getUserTimeZoneLabel } from '../../utils/datetime';
import { logDownloadFilename, triggerDownloadFromBlob } from '../../utils/download';
import './StreamingJobLogsModal.css';

type LiveLogPayload = {
  content?: string;
  status?: string;
  source?: string;
  lines?: string[];
  start_line?: number;
  end_line?: number;
  total_lines?: number;
};

type PersistedLogPayload = {
  content?: string;
  status?: string;
  source?: string;
};

type Props = {
  show: boolean;
  title: string;
  jobId?: number;
  initialStatus?: string;
  onHide: () => void;
  downloadName?: string;
};

const ACTIVE_JOB_STATUSES = new Set(['pending', 'queued', 'running', 'retrying']);
const LIVE_LOG_POLL_MS = 10000;
const SCROLL_THRESHOLD_PX = 48;

function isActiveJobStatus(status?: string): boolean {
  return ACTIVE_JOB_STATUSES.has((status || '').toLowerCase());
}

function joinLines(lines: string[]): string {
  return lines.join('\n');
}

function splitContent(content?: string): string[] {
  if (!content) {
    return [];
  }
  return content.split('\n');
}

function downloadText(filename: string, text: string): void {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  triggerDownloadFromBlob(blob, logDownloadFilename(filename, 'workflow.log'));
}

export default function StreamingJobLogsModal({
  show,
  title,
  jobId,
  initialStatus,
  onHide,
  downloadName,
}: Props): React.ReactElement {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(initialStatus || '');
  const [source, setSource] = useState('');
  const [lines, setLines] = useState<string[]>([]);
  const [content, setContent] = useState('');
  const [endLine, setEndLine] = useState(0);
  const [totalLines, setTotalLines] = useState(0);
  const [bufferedNewLines, setBufferedNewLines] = useState<string[]>([]);
  const [bufferedEndLine, setBufferedEndLine] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [errorText, setErrorText] = useState('');
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const isPinnedToBottomRef = useRef(true);
  const pendingScrollModeRef = useRef<'bottom' | 'none'>('none');

  const active = useMemo(() => isActiveJobStatus(status || initialStatus), [initialStatus, status]);
  const unreadCount = bufferedNewLines.length;

  const applySnapshot = useCallback((nextLines: string[], nextStatus?: string, nextSource?: string) => {
    setStatus(nextStatus || initialStatus || '');
    setSource(nextSource || '');
    setLines(nextLines);
    setContent(joinLines(nextLines));
    setEndLine(nextLines.length);
    setTotalLines(nextLines.length);
    setErrorText('');
  }, [initialStatus]);

  const fetchLiveLog = useCallback(async (params?: Record<string, number>) => {
    if (!jobId) {
      return null;
    }
    const response = await apiClient.get<LiveLogPayload>(API_ENDPOINTS.TEST_GENERATION_LIVE_LOG(jobId), {
      params: params || {},
    });
    return response.data;
  }, [jobId]);

  const fetchPersistedLog = useCallback(async () => {
    if (!jobId) {
      return null;
    }
    const response = await apiClient.get<PersistedLogPayload>(API_ENDPOINTS.TEST_GENERATION_LOGS(jobId));
    return response.data;
  }, [jobId]);

  const consumeBufferedLines = useCallback((scrollToBottom: boolean) => {
    if (!bufferedNewLines.length) {
      return;
    }

    const merged = [...lines, ...bufferedNewLines];
    pendingScrollModeRef.current = scrollToBottom ? 'bottom' : 'none';
    setLines(merged);
    setContent(joinLines(merged));
    setEndLine(bufferedEndLine ?? merged.length);
    setTotalLines(Math.max(totalLines, bufferedEndLine ?? merged.length));
    setBufferedNewLines([]);
    setBufferedEndLine(null);
  }, [bufferedEndLine, bufferedNewLines, lines, totalLines]);

  const loadInitial = useCallback(async () => {
    if (!show || !jobId) {
      return;
    }

    setLoading(true);
    setRefreshing(false);
    setBufferedNewLines([]);
    setBufferedEndLine(null);
    setLines([]);
    setContent('');
    setErrorText('');
    isPinnedToBottomRef.current = true;

    try {
      if (isActiveJobStatus(initialStatus)) {
        const payload = await fetchLiveLog();
        if (!payload) {
          return;
        }

        if (!isActiveJobStatus(payload.status)) {
          const persisted = await fetchPersistedLog();
          const nextLines = splitContent(persisted?.content ?? payload.content ?? '');
          applySnapshot(nextLines, persisted?.status || payload.status, persisted?.source || payload.source || 'persisted');
          return;
        }

        const nextLines = payload.lines ?? splitContent(payload.content);
        pendingScrollModeRef.current = 'bottom';
        applySnapshot(nextLines, payload.status, payload.source);
        setEndLine(payload.end_line ?? nextLines.length);
        setTotalLines(payload.total_lines ?? nextLines.length);
      } else {
        const payload = await fetchPersistedLog();
        const nextLines = splitContent(payload?.content ?? '');
        applySnapshot(nextLines, payload?.status || initialStatus, payload?.source || 'persisted');
      }
    } catch (error: any) {
      const message = error?.response?.data?.detail || error?.message || 'Log file not available.';
      setLines([]);
      setContent(message);
      setErrorText(message);
      setSource('error');
    } finally {
      setLoading(false);
    }
  }, [applySnapshot, fetchLiveLog, fetchPersistedLog, initialStatus, jobId, show]);

  const loadNewer = useCallback(async (forceToBottom: boolean) => {
    if (!active || !jobId) {
      return;
    }

    const cursor = bufferedEndLine ?? endLine;
    try {
      const payload = await fetchLiveLog({ after_line: cursor });
      if (!payload) {
        return;
      }

      if (!isActiveJobStatus(payload.status)) {
        const persisted = await fetchPersistedLog();
        const nextLines = splitContent(persisted?.content ?? payload.content ?? '');
        pendingScrollModeRef.current = forceToBottom ? 'bottom' : 'none';
        applySnapshot(
          nextLines,
          persisted?.status || payload.status || status || initialStatus,
          persisted?.source || payload.source || source || 'persisted',
        );
        setBufferedNewLines([]);
        setBufferedEndLine(null);
        return;
      }

      const nextLines = payload.lines ?? [];
      if (!nextLines.length) {
        setStatus(payload.status || status || initialStatus || '');
        setSource(payload.source || source);
        setTotalLines(payload.total_lines ?? totalLines);
        return;
      }

      if ((payload.source || '').toLowerCase() === 'pending' && cursor >= (payload.total_lines ?? 0)) {
        setStatus(payload.status || status || initialStatus || '');
        setSource(payload.source || source);
        setTotalLines(payload.total_lines ?? totalLines);
        return;
      }

      const shouldAppendNow = forceToBottom || isPinnedToBottomRef.current;
      if (shouldAppendNow) {
        const merged = [...lines, ...bufferedNewLines, ...nextLines];
        pendingScrollModeRef.current = 'bottom';
        applySnapshot(merged, payload.status || status || initialStatus, payload.source || source);
        setEndLine(payload.end_line ?? merged.length);
        setTotalLines(payload.total_lines ?? merged.length);
        setBufferedNewLines([]);
        setBufferedEndLine(null);
      } else {
        setStatus(payload.status || status || initialStatus || '');
        setSource(payload.source || source);
        setTotalLines(payload.total_lines ?? totalLines);
        setBufferedNewLines((prev) => [...prev, ...nextLines]);
        setBufferedEndLine(payload.end_line ?? cursor + nextLines.length);
      }
    } catch {
      // Keep the current view stable if polling fails.
    }
  }, [active, applySnapshot, bufferedEndLine, bufferedNewLines, endLine, fetchLiveLog, fetchPersistedLog, initialStatus, jobId, lines, source, status, totalLines]);

  const refreshLogs = useCallback(async () => {
    if (!jobId) {
      return;
    }

    setRefreshing(true);
    try {
      if (active) {
        await loadNewer(true);
      } else {
        const payload = await fetchPersistedLog();
        const nextLines = splitContent(payload?.content ?? '');
        applySnapshot(nextLines, payload?.status || status || initialStatus, payload?.source || source || 'persisted');
        setBufferedNewLines([]);
        setBufferedEndLine(null);
      }
    } catch (error: any) {
      const message = error?.response?.data?.detail || error?.message || 'Failed to refresh logs.';
      setErrorText(message);
    } finally {
      setRefreshing(false);
    }
  }, [active, applySnapshot, fetchPersistedLog, initialStatus, jobId, loadNewer, source, status]);

  useEffect(() => {
    setStatus(initialStatus || '');
  }, [initialStatus]);

  useEffect(() => {
    loadInitial().catch(() => undefined);
  }, [loadInitial]);

  useEffect(() => {
    if (!show || !active || !jobId) {
      return;
    }

    const interval = window.setInterval(() => {
      loadNewer(false).catch(() => undefined);
    }, LIVE_LOG_POLL_MS);
    return () => window.clearInterval(interval);
  }, [active, jobId, loadNewer, show]);

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) {
      return;
    }

    if (pendingScrollModeRef.current === 'bottom') {
      body.scrollTop = body.scrollHeight;
    }
    pendingScrollModeRef.current = 'none';
  }, [content, lines]);

  const handleScroll = useCallback(() => {
    const body = bodyRef.current;
    if (!body) {
      return;
    }

    const distanceFromBottom = body.scrollHeight - body.scrollTop - body.clientHeight;
    const pinned = distanceFromBottom <= SCROLL_THRESHOLD_PX;
    isPinnedToBottomRef.current = pinned;

    if (pinned) {
      consumeBufferedLines(true);
    }
  }, [consumeBufferedLines]);

  const renderedContent = loading ? null : content || errorText || 'No log entries yet.';
  const downloadContent = joinLines([...lines, ...bufferedNewLines]);

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="stream-log-modal__title">{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body className="stream-log-modal__body-wrap">
        {loading ? (
          <div className="stream-log-modal__loading">
            <Spinner animation="border" size="sm" />
          </div>
        ) : (
          <>
            <div
              ref={bodyRef}
              className="stream-log-modal__body"
              onScroll={handleScroll}
            >
              <pre className="stream-log-modal__pre">{renderedContent}</pre>
            </div>
            {unreadCount > 0 && (
              <div className="stream-log-modal__indicator-wrap">
                <button
                  type="button"
                  className="stream-log-modal__indicator"
                  onClick={() => consumeBufferedLines(true)}
                >
                  New logs available
                </button>
              </div>
            )}
          </>
        )}
        {!!status && (
          <p className="stream-log-modal__meta">
            Status: {status}
            {source ? ` | Source: ${source}` : ''}
            {totalLines > 0 ? ` | Loaded ${totalLines} ${totalLines === 1 ? 'line' : 'lines'}` : ''}
            {unreadCount > 0 ? ` | ${unreadCount} pending` : ''}
          </p>
        )}
        <p className="stream-log-modal__meta">Times shown in {getUserTimeZoneLabel()}</p>
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          className="stream-log-modal__btn"
          onClick={() => downloadText(downloadName || 'workflow.log', downloadContent)}
          disabled={loading}
        >
          Download
        </button>
        <button
          type="button"
          className="stream-log-modal__btn"
          onClick={() => refreshLogs().catch(() => undefined)}
          disabled={loading || refreshing}
        >
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
        <button
          type="button"
          className="stream-log-modal__btn stream-log-modal__btn--primary"
          onClick={onHide}
        >
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
}
